import React from 'react'
import { useBoolean } from 'react-use'

import { UsersAPI } from '@makyoapp/makyo-sdk/api/Users'
import { cleanEmail } from '@makyoapp/makyo-common'
import { Close8 } from '@makyoapp/makyo-icon/icon'
import { AvatarSize, FallbackAvatar, ImgEasyResult, UploadEasyCrop } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'

interface UploadAvatarProps {
  id?: string
  email?: string
  avatar?: string
  name?: string
  onRemove: () => void
  onChange: (nextResult: ImgEasyResult | null) => void
  isLoading?: boolean
  size?: AvatarSize
  btnStylePos?: boolean
  btnStyleClassName?: string
  btnStyleBorder?: boolean
  btnIconWrapperClassName?: string
  canEdit?: boolean
  withCache?: boolean
}
export function UploadAvatar({
  id,
  email,
  avatar,
  name,
  onRemove,
  onChange,
  isLoading,
  size,
  btnStylePos,
  btnStyleClassName,
  btnStyleBorder,
  btnIconWrapperClassName,
  canEdit = true,
  withCache = false,
}: UploadAvatarProps) {
  return (
    <div className='relative max-w-[112px]'>
      <FallbackAvatar
        faceAvatar={!!email}
        src={avatar}
        text={email ? cleanEmail(email) : name ? name : ''}
        alt={name || 'N/A'}
        email={email}
        size={size || AvatarSize.XXL}
        className='border'
        withCache={withCache}
      />
      {canEdit && avatar && (
        <button
          className='top-1 right-1 absolute bg-red-600 dark:bg-red-500 text-white w-6 h-6 flex-cc rounded-full'
          onClick={onRemove}
        >
          <Close8 />
        </button>
      )}
      {canEdit && (
        <UploadEasyCrop
          id={id}
          inputStyle={false}
          initialPreview={avatar}
          onChange={onChange}
          btnStylePos={btnStylePos}
          btnStyleClassName={btnStyleClassName}
          btnStyleBorder={btnStyleBorder}
          btnIconWrapperClassName={btnIconWrapperClassName}
          supportedExtensions={[]}
        />
      )}
      {canEdit && isLoading && (
        <div
          className='absolute inset-0 rounded-full flex-cc'
          style={{
            background: 'rgba(0,0,0,0.35)',
          }}
        >
          <span className='ltr:ml-3 rtl:mr-3 loading spin' />
        </div>
      )}
    </div>
  )
}

export function UploadUserAvatar({
  id,
  size,
  btnStylePos,
  btnStyleClassName,
  btnStyleBorder,
  btnIconWrapperClassName,
  onBeforeUpdate,
}: {
  id?: string
  size?: AvatarSize
  btnStylePos?: boolean
  btnStyleClassName?: string
  btnStyleBorder?: boolean
  btnIconWrapperClassName?: string
  onBeforeUpdate?: () => void
}) {
  const { userData, updateAvatar, avatar, setAvatar } = useAuth()

  const name = userData?.name?.trim()
  const email = userData?.email
  //const [avatar, setAvatar] = useState<string | undefined>(userData?.avatar)
  const [isAvatarLoading, setIsAvatarLoading] = useBoolean(false)
  const printAPIError = useReqError()

  // Add timestamp state for cache busting
  const [timestamp, setTimestamp] = React.useState<number>(Date.now())

  // Create two different avatar URLs: one for final state and one for preview
  const avatarWithTimestamp = avatar && !avatar.startsWith('blob:') ? `${avatar}?t=${timestamp}` : avatar

  return (
    <UploadAvatar
      id={id}
      avatar={avatarWithTimestamp}
      email={email}
      name={name}
      size={size}
      isLoading={isAvatarLoading}
      btnStylePos={btnStylePos}
      btnStyleClassName={btnStyleClassName}
      btnStyleBorder={btnStyleBorder}
      btnIconWrapperClassName={btnIconWrapperClassName}
      withCache={false}
      onChange={(nextResult) => {
        if (nextResult && nextResult.file) {
          const currentAvatar = userData?.avatar
          setAvatar(nextResult.src)
          toasted('Uploading avatar...')
          setIsAvatarLoading(true)
          onBeforeUpdate?.()
          UsersAPI.ReqUpdateAvatar(nextResult.file)
            .then(() => {
              if (userData) {
                updateAvatar(nextResult.src)
              }
              toasted('User avatar updated!')
            })
            .catch((e) => {
              URL.revokeObjectURL(nextResult.src)
              setAvatar(currentAvatar)
              toasted(e.message)
            })
            .finally(() => {
              setIsAvatarLoading(false)
            })
        }
      }}
      onRemove={() => {
        if (userData) {
          if (userData) {
            setIsAvatarLoading(true)
            UsersAPI.ReqDeleteAvatar()
              .then(() => {
                updateAvatar(undefined)
                setAvatar(undefined)
                toasted('Avatar removed!')
              })
              .catch((e) => {
                printAPIError(e)
              })
              .finally(() => setIsAvatarLoading(false))
          }
        }
      }}
    />
  )
}
